import { Injectable } from '@angular/core';

import { HttpClient } from '@angular/common/http';
import { Pagination, HttpPagination } from '@library/common/http';

import { timer, Observable, Subject, of, OperatorFunction, pipe } from 'rxjs';
import { map, tap, take, shareReplay } from 'rxjs/operators';

export const DefaultExpiryLength = 5; // Minutes

class RequestOptions<T, R> {
   url: string;
   params?: any;
   headers?: any;
   pipe?: OperatorFunction<T, R>;
   length?: number = DefaultExpiryLength;
   collection?: R;
   expiry?: number;
   refresher?: Subject<void>;
   cache?: string;
   pagination?: Pagination;
   useLambda?: boolean;

   constructor( opts: RequestOptions<any, any> ) {
      this.url = opts?.url;
      if ( opts?.params ) { this.params = opts.params; }

      this.pipe = opts?.pipe ?? pipe();
      this.length = opts?.length ?? DefaultExpiryLength;
      if ( opts?.collection ) { this.collection = opts.collection; }
      if ( opts?.expiry ) { this.expiry = opts.expiry; }
      if ( opts?.refresher ) { this.refresher = opts.refresher; }
      this.cache = opts.url + ( opts?.params ? JSON.stringify( opts.params ) : '' );
      if ( opts?.pagination ) { this.pagination = opts.pagination; }
      if ( opts?.headers ) { this.headers = opts.headers; }
      if ( opts?.useLambda ) { this.useLambda = opts.useLambda; }
   }
}

@Injectable({
   providedIn: 'root'
})
export class CacheService {

   cached = {};
   expiry = {};

   subscriptions: Map<string, RequestOptions<any, any>> = new Map();
   inFlight: Map<string, unknown> = new Map();
   
   loadFinish: Subject<string> = new Subject();
   /**
    * Request params (opts)
    * - url {string} - url the request will be sent to
    * - [params] {object} - required if request is POST
    * - [pipe] {OperatorFunction} - pipe that will be ran on returning data
    * - [refresher] {Subject<void>} - optional refresher to call when data is updated
    * - [length] {number} - length of time in minutes the cache will be stored ( default 5 )
    */
   request<T, R>( opts: RequestOptions<T, R> ): Observable<R> {
      opts = new RequestOptions( opts );
      if ( !opts.length ) { opts.length = DefaultExpiryLength; }

      if ( this.subscriptions.has( opts.cache )) {
         // we've got something in the cache
         const cached: RequestOptions<T, R> = this.subscriptions.get( opts.cache );

         // check the expiry and make sure we don't have a request already in flight
         if (( cached.expiry < new Date().getTime() ) && !this.inFlight.has( opts.cache )) {
            this.inFlight.set( opts.cache, this._request( cached ).pipe(
               take( 1 ),
            ).subscribe( _ => this.inFlight.delete( opts.cache )));
         }

         // returning cached data
         return of( cached.collection );
      }

      // we don't have someting in flight so set it up for a new request
      if ( !this.inFlight.has( opts.cache )) {
         // creating inFlight request
         this.inFlight.set( opts.cache, this._request( opts ));
      }

      // returing inFlight request
      return this.inFlight.get( opts.cache ) as Observable<R>;
   }

   private _request<T, R>( opts: RequestOptions<T, R> ) {
      return (
         opts.params ?
         this.http.post( opts.url, opts.params, { headers: opts.headers, params: { useLambda: opts.useLambda } } ) :
         this.http.get( opts.url, { headers: opts.headers, params: { useLambda: opts.useLambda } } )
      ).pipe(
         map(( response: any ) => {
            // checking to see if there is any pagination properties in the response object
            if ( response?.response?.pagination ) {
               if ( response.response.pagination.page === 1 ) {
                  // new pagination request, clear what we have cached (if anything)
                  this.subscriptions.delete( opts.cache );
               }

               const cached: RequestOptions<T, R> = this.subscriptions.get( opts.cache );
               if ( cached ) {
                  response.response.collection = [
                     ...response.response.collection,
                     ...( cached.collection as any || [] )
                  ];

                  // request has been cancelled
                  if ( response.response.pagination.page !== 1 && !cached.pagination ) {
                     return response;
                  }
               }

               opts.pagination = response.response.pagination;
               this._paginate( opts );
            }

            return response;
         }),
         opts.pipe, // running the passed pipes
         tap( collection => {
            const cached = this.subscriptions.get( opts.cache ) ?? opts;

            // let's cache the piped data
            this.subscriptions.set( opts.cache, ({
               ...cached,
               collection,
               expiry: new Date().getTime() + ( opts.length * 60 * 1000 )
            }))
         }),
         tap( _ => this.inFlight.delete( opts.cache )),
         take( 1 ), // cleaning up leftover subscriptions since we are handling this through the cache
         tap( _ => { if ( opts.refresher ) { opts.refresher.next(); }}),
         shareReplay(),
      );
   }

   private _paginate<T, R>( opts: RequestOptions<T, R> ): RequestOptions<T, R> {
      if ( !opts?.pagination ) { return; } // need an object
      
      this.loadFinish.next('pending'); // mark pending if data is still requesting from backend
      if ( opts.pagination.page >= opts.pagination.total ) {  // we've reached the end of the pagination
         this.loadFinish.next('finished');
         this.cancelPagination( opts?.pagination?.hash );

         this.pagination.reset();

         return opts;
      }

      // update the http pagination object
      this.pagination.hash = opts.pagination.hash;
      this.pagination.loading = true;
      this.pagination.loadingPercent = +( opts.pagination.page / opts.pagination.total * 100 ).toFixed();

      // next page
      opts.pagination.page++;
      opts.params.pagination = opts.pagination;

      this._request<T, R>( opts ).pipe( take( 1 ), shareReplay() ).subscribe(); // take 1 for cleanup
   }

   cancelPagination( hash: string ) {
      for ( const [ key, value ] of this.subscriptions ) {
         if ( value.pagination?.hash === hash ) {
            delete( value.pagination );
            if ( value?.params?.pagination ) {
               delete( value?.params?.pagination );
            }

            this.subscriptions.set( key, value );
         }
      }
   }

   constructor( private http: HttpClient, public pagination: HttpPagination ) {
      this.pagination.stop$.subscribe( hash => this.cancelPagination( hash ));
   }

   observable<T>( property: string ): Observable<T> {
      if ( this.check( property )) {
         return of( this.retrieve( property ));
      }
   }

   check( property: string ): boolean {
      if ( !this.expiry[property] ) { delete( this.cached[property] ); }

      return !!this.expiry[property];
   }

   retrieve( property: string ): any {
      if ( !this.check( property )) { return; }

      return this.cached[property];
   }

   /** length is in minutes */
   store( property: string, data: any, length: number = DefaultExpiryLength ): void {
      this.cached[property] = data;
      if ( this.expiry[property] ) { this.expiry[property].unsubscribe(); }
      this.expiry[property] = timer( length * 60 * 1000 ).subscribe( _ => this.expire( property ));
   }

   clearSet( property: string ) {
      if ( !property ) { return; }
      Object.keys( this.expiry )
         .filter( prop => prop.includes( property ))
         .map( prop => this.expire( prop ));

      for ( const key of this.subscriptions.keys() ) {
         if ( key.includes( property )) {
            this.subscriptions.delete( key );
         }
      }

      for ( const key of this.inFlight.keys() ) {
         if ( key.includes( property )) {
            this.inFlight.delete( key );
         }
      }
   }

   expire( property: string ): void {
      if ( this.expiry[property] ) {
         this.expiry[property].unsubscribe();
         delete( this.expiry[property] );
      }

      this.inFlight.delete( property );
      this.subscriptions.delete( property );
   }

   reset(): void {
      this.cached = {};
      Object.keys( this.expiry ).map( key => this.expiry[key].unsubscribe() );
      this.expiry = {};
      this.subscriptions = new Map();
      this.inFlight = new Map();
   }
}
